import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Clock, Users, Coins, BadgeCheck, Rocket, LogOut, Trash2, CheckCheck, Star, Smile, Meh, Frown, Flag, Plus, MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const PASTELS = { sports:"#E2F3E4", social:"#FFE4E1", educational:"#EBE4FF", entertainment:"#FFF0D4" };

export default function ActivityDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [act, setAct] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showJoin, setShowJoin] = useState(false);
  const [joinMsg, setJoinMsg] = useState("");
  const [bringFriend, setBringFriend] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showRate, setShowRate] = useState(null);
  const [showMood, setShowMood] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/activities/${id}`);
      setAct(data);
      if (user && data.host_id === user.id) {
        const { data: r } = await api.get("/requests/incoming");
        setRequests(r.filter((x) => x.activity_id === id));
      }
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  if (!act) return <div className="p-8 text-center">{t("common.loading")}</div>;

  const isHost = user && act.host_id === user.id;
  const isParticipant = user && act.accepted_user_ids?.includes(user.id);
  const seatsLeft = act.seats_left ?? Math.max(0, act.capacity - (act.accepted_user_ids?.length || 0));
  const full = seatsLeft === 0;
  const completed = act.status === "completed";
  const isModerator = user?.role === "admin" && (user?.is_master || user?.email === "mustafahaidari03@gmail.com" || (user?.admin_permissions || []).includes("reports") || (user?.admin_permissions || []).includes("users"));
  const date = act.start_at ? new Date(act.start_at) : null;
  const bg = PASTELS[act.category] || "#D4F1F4";

  const join = async () => {
    setBusy(true);
    try {
      await api.post("/requests", { activity_id: id, message: joinMsg, bring_friend_count: Number(bringFriend) });
      toast.success(t("activityDetail.requestSent"));
      setShowJoin(false); setJoinMsg(""); setBringFriend(0);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "not_enough_coins") {
        toast.error(t("activityDetail.needCoins", { needed: detail.needed }));
        setTimeout(() => nav("/coins"), 1500);
      } else {
        toast.error(fmtError(detail) || e.message);
      }
    }
    finally { setBusy(false); }
  };

  const decide = async (rid, action) => {
    try {
      await api.post(`/requests/${rid}/${action}`);
      toast.success(action === "accept" ? t("activityDetail.accept") + " ✓" : t("activityDetail.reject") + " ✓");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "requester_not_enough_coins") {
        toast.error(t("activityDetail.requesterNoCoins", { name: detail.name }));
      } else {
        toast.error(fmtError(detail) || e.message);
      }
    }
  };

  const boost = async () => {
    try { await api.post(`/activities/${id}/boost`); toast.success(t("activityDetail.boostToast")); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const leave = async () => {
    if (!confirm(t("activityDetail.leaveConfirm"))) return;
    try { await api.post(`/activities/${id}/leave`); toast.success(t("activityDetail.leftToast")); nav("/feed"); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!confirm(t("activityDetail.deleteConfirm"))) return;
    try { await api.delete(`/activities/${id}`); toast.success(t("activityDetail.deletedToast")); nav("/feed"); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const complete = async () => {
    try { await api.post(`/activities/${id}/complete`); toast.success(t("activityDetail.completedToast")); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const submitRate = async (stars) => {
    try {
      await api.post("/ratings", { activity_id: id, rated_id: showRate.id, stars, comment: "" });
      toast.success(t("activityDetail.rated", { name: showRate.name }));
      setShowRate(null);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const submitMood = async (mood) => {
    try { await api.post("/moods", { activity_id: id, mood }); toast.success(t("activityDetail.moodLogged")); setShowMood(false); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const report = async () => {
    const reason = prompt(t("activityDetail.reportReason"));
    if (!reason) return;
    try { await api.post("/reports", { target_id: act.host_id, reason }); toast.success(t("activityDetail.reported")); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const openChat = async () => {
    try {
      const { data: chats } = await api.get("/chats");
      const c = chats.find((x) => x.activity_id === id);
      if (c) nav(`/chats/${c.id}`); else toast.error(t("activityDetail.noChat"));
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={3}/> {t("common.back")}</button>      <div 
        className={`card-nb mb-5 overflow-hidden ${act.cover_image ? "p-0 bg-white" : "p-6"}`} 
        style={{ background: act.cover_image ? "white" : bg }}
        data-testid="activity-hero"
      >
        {act.cover_image && (
          <div className="relative h-64 w-full overflow-hidden border-b-2 border-[#121212]">
            <img src={act.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        
        <div className={act.cover_image ? "p-6" : ""}>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="tag-nb bg-white">{t(`feedPage.cat.${act.category}`, act.category)}</span>
            {act.boosted && <span className="tag-nb bg-[#F9C349]"><Rocket className="w-3 h-3" strokeWidth={3}/> {t("activityDetail.boostedTag")}</span>}
            {completed && <span className="tag-nb bg-[#121212] text-white">{t("activityDetail.completedTag")}</span>}
            {full && !completed && <span className="tag-nb bg-[#FF6B6B] text-white">{t("activityDetail.fullTag")}</span>}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl leading-tight mb-3">{act.title}</h1>
          {act.description && <p className="text-[#121212] mb-4 whitespace-pre-wrap">{act.description}</p>}
          <div className="flex flex-wrap gap-2 mb-4">
            {act.location && <span className="tag-nb bg-white"><MapPin className="w-3 h-3" strokeWidth={3}/> {act.location}</span>}
            {date && <span className="tag-nb bg-white"><Clock className="w-3 h-3" strokeWidth={3}/> {date.toLocaleString()}</span>}
            <span className="tag-nb bg-white"><Users className="w-3 h-3" strokeWidth={3}/> {t("activityDetail.leftCount", { n: seatsLeft, total: act.capacity })}</span>
            {act.price > 0 && <span className="tag-nb bg-[#F9C349]"><Coins className="w-3 h-3" strokeWidth={3}/> {act.price} T-Coins</span>}
          </div>

          {act.host && (
            <Link to={`/u/${act.host.id}`} className="flex items-center gap-3 p-3 bg-white border-2 border-[#121212] rounded-2xl mb-4" data-testid="host-card">
              <div className="w-12 h-12 rounded-full bg-[#FFF0D4] border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden shrink-0">
                {act.host.avatar_url ? <img src={act.host.avatar_url} alt="" className="w-full h-full object-cover"/> : act.host.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 font-display font-bold">
                  {act.host.name} {act.host.verified && <BadgeCheck className="w-4 h-4 text-[#00B4D8]" strokeWidth={3}/>}
                </div>
                <div className="text-xs text-[#4A4A4A]">★ {act.host.rating_avg?.toFixed(1) || "—"} · {act.host.completed_count || 0} {t("profile.done")} · {t("profile.trust")} {act.host.reliability_score ?? 100}</div>
              </div>
            </Link>
          )}

          <div className="flex flex-wrap gap-2">
            {!isHost && !isParticipant && !full && !completed && (
              <button onClick={() => setShowJoin(true)} className="btn-nb btn-nb-primary" data-testid="ask-to-join-btn">{t("activityDetail.askToJoin")}</button>
            )}
            {isParticipant && !isHost && !completed && (
              <>
                <button onClick={openChat} className="btn-nb btn-nb-info" data-testid="open-chat-btn"><MessageCircle className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.groupChat")}</button>
                <button onClick={() => nav(`/activity/${id}/map`)} className="btn-nb btn-nb-info" data-testid="live-map-btn"><MapPin className="w-4 h-4" strokeWidth={3}/> Live map</button>
                <button onClick={leave} className="btn-nb" data-testid="leave-btn"><LogOut className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.silentExit")}</button>
              </>
            )}
            {isHost && !completed && (
              <>
                <button onClick={openChat} className="btn-nb btn-nb-info" data-testid="open-chat-btn"><MessageCircle className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.groupChat")}</button>
                <button onClick={() => nav(`/activity/${id}/map`)} className="btn-nb btn-nb-info" data-testid="live-map-btn"><MapPin className="w-4 h-4" strokeWidth={3}/> Live map</button>
                <button onClick={boost} className="btn-nb btn-nb-warn" data-testid="boost-btn"><Rocket className="w-4 h-4" strokeWidth={3}/> {act.boosted ? t("activityDetail.boosted") : t("activityDetail.boostedDemo")}</button>
                <button onClick={complete} className="btn-nb btn-nb-dark" data-testid="complete-btn"><CheckCheck className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.markDone")}</button>
                <button onClick={del} className="btn-nb" data-testid="delete-btn"><Trash2 className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.delete")}</button>
              </>
            )}
            {!isHost && isModerator && (
              <button onClick={del} className="btn-nb btn-nb-coral" data-testid="delete-btn"><Trash2 className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.delete")}</button>
            )}
            {completed && isParticipant && (
              <button onClick={() => setShowMood(true)} className="btn-nb btn-nb-warn" data-testid="mood-btn"><Smile className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.logMood")}</button>
            )}
            {completed && isHost && (
              <button onClick={del} className="btn-nb" data-testid="delete-btn"><Trash2 className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.delete")}</button>
            )}
            {!isHost && <button onClick={report} className="btn-nb" data-testid="report-btn"><Flag className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.report")}</button>}
          </div>
        </div>
      </div>

      <div className="card-nb p-6 mb-5 bg-white">
        <h3 className="font-display font-black text-xl mb-3">{t("activityDetail.squad")} ({act.accepted_user_ids?.length || 0})</h3>
        <ParticipantsList ids={act.accepted_user_ids || []} hostId={act.host_id} completed={completed} currentUserId={user?.id} onRate={(u) => setShowRate(u)} />
      </div>

      {isHost && requests.length > 0 && (
        <div className="card-nb p-6 mb-5 bg-[#FFF0D4]" data-testid="incoming-requests">
          <h3 className="font-display font-black text-xl mb-3">{t("activityDetail.requests")} ({requests.filter(r => r.status === 'pending').length})</h3>
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="flex items-start gap-3 p-3 bg-white border-2 border-[#121212] rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-[#EBE4FF] border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden shrink-0">
                  {r.requester?.avatar_url ? <img src={r.requester.avatar_url} alt="" className="w-full h-full object-cover"/> : r.requester?.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{r.requester?.name} <span className="text-xs text-[#4A4A4A]">({r.status})</span></div>
                  {r.bring_friend_count > 0 && <div className="text-xs text-[#FF6B6B] font-bold">+{r.bring_friend_count} friend(s)</div>}
                  {r.message && <div className="text-sm text-[#4A4A4A] italic">&ldquo;{r.message}&rdquo;</div>}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => decide(r.id, "accept")} className="btn-nb btn-nb-info text-xs" data-testid={`accept-${r.id}`}>{t("activityDetail.accept")}</button>
                    <button onClick={() => decide(r.id, "reject")} className="btn-nb text-xs" data-testid={`reject-${r.id}`}>{t("activityDetail.reject")}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showJoin && (
        <Modal onClose={() => setShowJoin(false)} title={t("activityDetail.askToJoin")}>
          {act.price > 0 && (
            <div className="mb-3 p-3 rounded-xl bg-[#FFF0D4] border-2 border-[#121212]" data-testid="join-price-note">
              <div className="text-sm font-bold flex items-center gap-1.5">
                <Coins className="w-4 h-4" strokeWidth={3}/> {t("activityDetail.joinCostNote", { cost: act.price })}
              </div>
              <div className="text-xs font-semibold mt-1 text-[#4A4A4A]">
                {t("activityDetail.yourBalance")}: {user?.coins ?? 0} 🪙
              </div>
            </div>
          )}
          <textarea className="input-nb min-h-[80px]" placeholder={t("activityDetail.sayHi")} value={joinMsg} onChange={(e) => setJoinMsg(e.target.value)} data-testid="join-msg-input"/>
          <div className="mt-3">
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("activityDetail.bringFriend")}</label>
            <div className="flex gap-2">
              {[0,1,2,3].map((n) => (
                <button key={n} type="button" onClick={() => setBringFriend(n)} className={`btn-nb ${bringFriend === n ? "btn-nb-warn" : ""}`} data-testid={`bring-${n}`}>+{n}</button>
              ))}
            </div>
          </div>
          <button onClick={join} disabled={busy} className="btn-nb btn-nb-primary w-full mt-4" data-testid="send-request-btn">
            {busy ? t("activityDetail.sending") : t("activityDetail.sendRequest")}
          </button>
        </Modal>
      )}

      {showRate && (
        <Modal onClose={() => setShowRate(null)} title={t("activityDetail.rateName", { name: showRate.name })}>
          <div className="flex justify-center gap-2 my-4">
            {[1,2,3,4,5].map((s) => (
              <button key={s} onClick={() => submitRate(s)} className="text-4xl hover:scale-125 transition-transform" data-testid={`rate-${s}`}>
                <Star className="w-10 h-10 text-[#F9C349]" strokeWidth={2.5} fill="#F9C349"/>
                <span className="sr-only">{s}</span>
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-[#4A4A4A]">{t("activityDetail.tapStar")}</p>
        </Modal>
      )}

      {showMood && (
        <Modal onClose={() => setShowMood(false)} title={t("activityDetail.howWasIt")}>
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => submitMood("great")} className="btn-nb btn-nb-info py-6 flex-col" data-testid="mood-great"><Smile className="w-8 h-8"/> {t("activityDetail.great")}</button>
            <button onClick={() => submitMood("average")} className="btn-nb btn-nb-warn py-6 flex-col" data-testid="mood-average"><Meh className="w-8 h-8"/> {t("activityDetail.ok")}</button>
            <button onClick={() => submitMood("bad")} className="btn-nb py-6 flex-col" data-testid="mood-bad"><Frown className="w-8 h-8"/> {t("activityDetail.bad")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ParticipantsList({ ids, hostId, completed, currentUserId, onRate }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.all(ids.map((id) => api.get(`/users/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => { if (alive) setUsers(arr.filter(Boolean)); });
    return () => { alive = false; };
  }, [ids.join(",")]);
  return (
    <div className="grid grid-cols-2 gap-3">
      {users.map((u) => (
        <div key={u.id} className="flex items-center gap-2 p-2 bg-[#F4F1EA] border-2 border-[#121212] rounded-2xl">
          <Link to={`/u/${u.id}`} className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-white border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden shrink-0">
              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover"/> : u.name?.[0]?.toUpperCase()}
            </div>
            <div className="text-sm font-bold truncate flex items-center gap-1">
              {u.name}
              {u.id === hostId && <span className="text-[10px] tag-nb bg-[#F9C349] !py-0">{t("activityDetail.host")}</span>}
              {u.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#00B4D8]" strokeWidth={3}/>}
            </div>
          </Link>
          {completed && currentUserId !== u.id && (
            <button onClick={() => onRate(u)} className="btn-nb !py-1 !px-2 text-xs shrink-0" data-testid={`rate-btn-${u.id}`}>
              <Star className="w-3 h-3" strokeWidth={3}/>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 bg-[#121212]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-nb p-6 max-w-md w-full bg-white animate-pop" onClick={(e) => e.stopPropagation()} data-testid="modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-black text-xl">{title}</h3>
          <button onClick={onClose} className="btn-nb !p-2" data-testid="modal-close"><Plus className="w-4 h-4 rotate-45"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
